import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useReveal, useBentoGlow } from '../hooks/useScrollReveal';
import '../styles/Skills.css';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

const Skills = () => {
    const [ref, isVisible] = useReveal();
    const glowRef = useBentoGlow();
    const [activeTab, setActiveTab] = useState('cat-front');
    const [selectedSkill, setSelectedSkill] = useState(null);

    const skillCategories = [
        {
            id: 'cat-front',
            title: 'FRONTEND',
            skills: [
                { 
                    name: 'React.js', 
                    icon: '/react.webp', 
                    desc: 'Penguasaan ekosistem React untuk membangun Single Page Applications (SPA) yang sangat interaktif, modular, dan mengutamakan performa rendering tingkat tinggi.',
                    tags: [
                        { text: 'SPA Architecture', level: 'red' },
                        { text: 'React Hooks', level: 'red' },
                        { text: 'Custom Hooks', level: 'yellow' },
                        { text: 'Component Design', level: 'red' },
                        { text: 'State Management', level: 'yellow' },
                        { text: 'Context API', level: 'green' }
                    ]
                },
                { 
                    name: 'Next.js', 
                    icon: '/next.webp', 
                    desc: 'Ahli dalam Server-Side Rendering (SSR), Static Site Generation (SSG), dan optimalisasi arsitektur web modern untuk performa SEO maksimal dan loading kilat.',
                    tags: [
                        { text: 'SSR & SSG', level: 'red' },
                        { text: 'App Router', level: 'red' },
                        { text: 'SEO Optimization', level: 'yellow' },
                        { text: 'API Routes', level: 'yellow' },
                        { text: 'Middleware', level: 'green' }
                    ]
                },
                { 
                    name: 'JavaScript', 
                    icon: '/js.webp', 
                    desc: 'Eksplorasi mendalam pada fondasi vanilla JS, manipulasi DOM asinkron, dan implementasi algoritma kompleks di sisi klien.',
                    tags: [
                        { text: 'ES6+', level: 'red' },
                        { text: 'DOM Manipulation', level: 'red' },
                        { text: 'Async/Await', level: 'red' },
                        { text: 'Event Loop', level: 'yellow' },
                        { text: 'Closures', level: 'green' }
                    ]
                },
                { 
                    name: 'TypeScript', 
                    icon: '/ts.webp', 
                    desc: 'Mengintegrasikan static typing yang ketat untuk arsitektur kode yang dapat diprediksi, minim bug, dan siap untuk skalabilitas skala besar.',
                    tags: [
                        { text: 'Static Typing', level: 'yellow' },
                        { text: 'Interfaces', level: 'yellow' },
                        { text: 'Generics', level: 'green' },
                        { text: 'Type Inference', level: 'green' }
                    ]
                },
                { 
                    name: 'Vite', 
                    icon: '/vite.webp', 
                    desc: 'Implementasi build tool modern untuk Hot Module Replacement (HMR) instan dan efisiensi development workflow.',
                    tags: [
                        { text: 'Fast Bundling', level: 'red' },
                        { text: 'HMR', level: 'yellow' },
                        { text: 'Rollup Config', level: 'green' }
                    ]
                },
                { 
                    name: 'HTML5/CSS3', 
                    icon: '/html.webp', 
                    desc: 'Fondasi semantic web yang accessible, dikombinasikan dengan arsitektur CSS presisi tinggi untuk tata letak yang pixel-perfect.',
                    tags: [
                        { text: 'Semantic Web', level: 'red' },
                        { text: 'Tailwind CSS', level: 'red' },
                        { text: 'Flexbox & Grid', level: 'red' },
                        { text: 'Responsive Design', level: 'yellow' },
                        { text: 'Keyframe Animations', level: 'green' }
                    ]
                }
            ]
        },
        {
            id: 'cat-back',
            title: 'BACKEND & CLOUD',
            skills: [
                { 
                    name: 'Node.js', 
                    icon: '/node.webp', 
                    desc: 'Perancangan dan pembuatan REST API yang scalable, pengolahan backend logic, dan manajemen arsitektur server-side yang efisien.',
                    tags: [
                        { text: 'RESTful API', level: 'yellow' },
                        { text: 'Express.js', level: 'yellow' },
                        { text: 'Middleware Logic', level: 'green' },
                        { text: 'JWT Auth', level: 'green' }
                    ]
                },
                { 
                    name: 'MongoDB', 
                    icon: '/mongo.webp', 
                    desc: 'Manajemen database non-relasional tingkat lanjut untuk penyimpanan data yang fleksibel, cepat, dan terdistribusi dengan baik.',
                    tags: [
                        { text: 'NoSQL', level: 'yellow' },
                        { text: 'Mongoose', level: 'yellow' },
                        { text: 'Schema Design', level: 'green' },
                        { text: 'Aggregation', level: 'green' }
                    ]
                },
                { 
                    name: 'Firebase', 
                    icon: '/fire.webp', 
                    desc: 'Integrasi ekosistem Backend-as-a-Service untuk autentikasi user, penyimpanan data real-time, dan analitik aplikasi.',
                    tags: [
                        { text: 'Firestore', level: 'red' },
                        { text: 'Authentication', level: 'red' },
                        { text: 'Hosting', level: 'yellow' },
                        { text: 'Realtime DB', level: 'green' }
                    ]
                },
                { 
                    name: 'Docker / Vercel', 
                    icon: '/doc.webp', 
                    desc: 'Mengisolasi environment pengembangan menggunakan kontainer untuk memastikan konsistensi deployment di berbagai infrastruktur server.',
                    tags: [
                        { text: 'CI/CD Deployment', level: 'red' },
                        { text: 'Edge Functions', level: 'yellow' },
                        { text: 'Containerization', level: 'green' },
                        { text: 'Dockerfile', level: 'green' }
                    ]
                },
                { 
                    name: 'Python / GraphQL', 
                    icon: '/python.webp', 
                    desc: 'Pengembangan skrip otomatisasi tingkat lanjut, manipulasi data kompleks, dan integrasi logika machine learning ringan.',
                    tags: [
                        { text: 'Data Automation', level: 'yellow' },
                        { text: 'Scripting', level: 'green' },
                        { text: 'Query Optimization', level: 'green' },
                        { text: 'Apollo', level: 'green' }
                    ]
                }
            ]
        },
        {
            id: 'cat-edit',
            title: 'DESIGN & VIDEO',
            skills: [
                { 
                    name: 'Figma', 
                    icon: '/figma.webp', 
                    desc: 'Perancangan tata letak antarmuka modern, wireframing, dan pembuatan purwarupa (prototyping) interaktif yang berfokus pada pengalaman pengguna.',
                    tags: [
                        { text: 'UI/UX Prototyping', level: 'red' },
                        { text: 'Auto Layout', level: 'red' },
                        { text: 'Design Systems', level: 'yellow' },
                        { text: 'Wireframing', level: 'yellow' }
                    ]
                },
                { 
                    name: 'Premiere Pro & After Effects', 
                    icon: '/pr.webp', 
                    desc: 'Pengeditan video timeline-based tingkat profesional, koreksi warna, dan perakitan aset visual untuk produksi multimedia berkualitas tinggi.',
                    tags: [
                        { text: 'Timeline Editing', level: 'yellow' },
                        { text: 'Color Grading', level: 'yellow' },
                        { text: 'Motion Graphics', level: 'green' },
                        { text: 'Keyframing', level: 'green' }
                    ]
                }
            ]
        },
        {
            id: 'cat-office',
            title: 'TOOLS & NETWORKING',
            skills: [
                { 
                    name: 'Git & GitHub', 
                    icon: '/git.webp', 
                    desc: 'Penguasaan sistem manajemen versi (version control) untuk melacak perubahan kode secara aman dan terstruktur.',
                    tags: [
                        { text: 'Version Control', level: 'red' },
                        { text: 'Team Collaboration', level: 'yellow' },
                        { text: 'Merge Resolution', level: 'yellow' },
                        { text: 'CI Actions', level: 'green' }
                    ]
                },
                { 
                    name: 'IoT & Networking', 
                    icon: '/iot.webp', 
                    desc: 'Pemahaman mendalam terkait topologi jaringan, integrasi mikrokontroler, serta konfigurasi tingkat lanjut pada arsitektur MikroTik.',
                    tags: [
                        { text: 'MikroTik Config', level: 'red' },
                        { text: 'Routing Protocol', level: 'yellow' },
                        { text: 'Switching', level: 'yellow' },
                        { text: 'Hardware Integration', level: 'green' }
                    ]
                }
            ]
        }
    ];

    const activeContent = skillCategories.find(c => c.id === activeTab);

    return (
        <section id="skills" className="skills" ref={ref}>
            <div className="container" ref={glowRef}>
                {isVisible && (
                    <motion.div
                        className="skills-tab-layout"
                        initial="hidden"
                        animate="visible"
                        variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
                    >
                        <motion.div className="skills-header" variants={fadeUp}>
                            <span className="section-label">Expertise</span>
                            <h2 className="section-heading">Skills & <span className="gradient-text">Tech Stack</span></h2>
                        </motion.div>

                        <motion.div className="tab-container flex flex-col md:flex-row gap-6 md:gap-12" variants={fadeUp}>
                            <div className="tab-menu-sidebar flex flex-row overflow-x-auto whitespace-nowrap gap-2 pb-2 md:flex-col [&::-webkit-scrollbar]:hidden scrollbar-hide">
                                {skillCategories.map((cat) => (
                                    <button
                                        key={cat.id}
                                        className={`tab-btn text-sm px-4 py-2 md:text-base md:px-6 md:py-3 ${activeTab === cat.id ? 'active' : ''}`}
                                        onClick={() => setActiveTab(cat.id)}
                                    >
                                        {cat.title}
                                    </button>
                                ))}
                            </div>
                            
                            <div className="tab-content-area w-full">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={activeTab}
                                        className="app-grid-container"
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <div className="app-grid grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4 md:gap-6">
                                            {activeContent.skills.map((skill, index) => (
                                                <div 
                                                    key={index} 
                                                    className="app-item-wrapper"
                                                    onClick={() => setSelectedSkill(skill)}
                                                >
                                                    <motion.div 
                                                        className="app-icon-card"
                                                        whileHover={{ scale: 1.1, y: -5 }}
                                                        whileTap={{ scale: 0.9 }}
                                                    >
                                                        <img src={skill.icon} alt={skill.name} className="app-icon-img" />
                                                    </motion.div>
                                                    <span className="app-name">{skill.name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </div>

            {/* Final Skill Detail Modal */}
            <AnimatePresence>
                {selectedSkill && (
                    <div className="skill-modal-root">
                        <motion.div 
                            className="skill-modal-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setSelectedSkill(null)}
                        />
                        <motion.div 
                            className="skill-modal-content bento-card"
                            initial={{ opacity: 0, scale: 0.8, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.8, y: 20 }}
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        >
                            
                            <div className="modal-icon-header">
                                <img src={selectedSkill.icon} alt="logo" className="modal-large-icon" />
                            </div>
                            
                            <h2 className="modal-skill-title">{selectedSkill.name}</h2>
                            <p className="modal-skill-desc-justify">{selectedSkill.desc}</p>
                            
                            <div className="modal-tags-wrapper">
                                {selectedSkill.tags.map((tag, idx) => (
                                    <span key={idx} className={`skill-tag-badge tag-level-${tag.level}`}>
                                        {tag.text}
                                    </span>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </section>
    );
};

export default Skills;